import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment-timezone';
import API_URL from '../config';
import SharedNav from './SharedNav';
import './Reports.css';

function Reports({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState([]);
  const [activity, setActivity] = useState({ fullView: true, rows: [], assignments: {} });
  const [faByRecruiterCompany, setFaByRecruiterCompany] = useState([]);
  const [faChatsByCompany, setFaChatsByCompany] = useState({});
  const [expanded, setExpanded] = useState(() => new Set());

  // Default to last 30 days in Eastern Time
  const getDefaultEndDate = () => {
    return moment().tz('America/New_York').format('YYYY-MM-DD');
  };

  const getDefaultStartDate = () => {
    return moment().tz('America/New_York').subtract(30, 'days').format('YYYY-MM-DD');
  };

  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const q = `startDate=${startDate}&endDate=${endDate}`;
      const [perfResp, actResp, faResp] = await Promise.allSettled([
        axios.get(`${API_URL}/api/reports/recruiter-performance?${q}`, { headers }),
        axios.get(`${API_URL}/api/reports/recruiter-activity?${q}`, { headers }),
        axios.get(`${API_URL}/api/reports/fa-knowledge-stats?${q}`, { headers }),
      ]);

      if (perfResp.status === 'fulfilled') setReportData(perfResp.value.data);
      else console.error('Error fetching performance data:', perfResp.reason);

      if (actResp.status === 'fulfilled') setActivity(actResp.value.data);
      else console.error('Error fetching recruiter activity:', actResp.reason);

      if (faResp.status === 'fulfilled') {
        setFaByRecruiterCompany(faResp.value.data.byRecruiterCompany || []);
        const companyMap = {};
        for (const row of faResp.value.data.byCompany || []) {
          companyMap[row.company_name] = row.fa_chat_count;
        }
        setFaChatsByCompany(companyMap);
      } else {
        console.error('Error fetching FA knowledge stats:', faResp.reason);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching report data:', err);
      setLoading(false);
    }
  };

  const pct = (num, den) => (den > 0 ? Math.round((num / den) * 100) : 0);

  const firstName = (name) => (name || '').trim().split(/\s+/)[0] || name;

  const aggregate = (groupKey) => {
    const totals = {};
    reportData.forEach(row => {
      const key = row[groupKey] || 'Unassigned';
      if (!totals[key]) totals[key] = { screened: 0, rating_5_plus: 0, fadv_sent: 0, ready_for_interview: 0, hired: 0 };
      totals[key].screened         += Number(row.candidates_screened) || 0;
      totals[key].rating_5_plus    += Number(row.rating_5_plus)       || 0;
      totals[key].fadv_sent        += Number(row.fadv_sent)           || 0;
      totals[key].ready_for_interview += Number(row.ready_for_interview) || 0;
      totals[key].hired             += Number(row.hired)               || 0;
    });
    return totals;
  };

  const companyTotals = aggregate('company_name');

  const grandTotal = (totalsObj) => Object.values(totalsObj).reduce((acc, t) => {
    acc.screened += t.screened;
    acc.rating_5_plus += t.rating_5_plus;
    acc.fadv_sent += t.fadv_sent;
    acc.ready_for_interview += t.ready_for_interview;
    acc.hired += t.hired;
    return acc;
  }, { screened: 0, rating_5_plus: 0, fadv_sent: 0, ready_for_interview: 0, hired: 0 });

  const companyGrandTotal = grandTotal(companyTotals);
  const faChatsCompanyTotal = Object.values(faChatsByCompany).reduce((a, n) => a + (Number(n) || 0), 0);

  const fullView = activity.fullView !== false;
  const recruiters = buildRecruiters(reportData, activity, faByRecruiterCompany);
  const teamTotal = recruiters.reduce((acc, r) => addMetrics(acc, r.total), emptyMetrics());
  const allExpanded = recruiters.length > 0 && recruiters.every(r => expanded.has(r.id));
  const toggleRecruiter = id => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(recruiters.map(r => r.id)));

  return (
    <div className="reports-container">
      <SharedNav user={user} onLogout={onLogout} />

      <div className="reports-content">
        <div className="date-filter-section">
          <h3>Select Date Range</h3>
          <div className="date-filters">
            <div className="date-input-group">
              <label>Start Date:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="date-input-group">
              <label>End Date:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <button onClick={fetchReportData} className="refresh-btn">
              Refresh Data
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading report data...</div>
        ) : (
          <>
            <div className="detail-tables-row">

              {/* By Recruiter: calls & texts (RingCentral) + pipeline, expandable by customer */}
              <div className="table-section">
                <div className="report-section-head">
                  <h3>Performance by Recruiter</h3>
                  {!fullView && <span className="report-scope-note">Top 3 by calls and texts, plus you</span>}
                  {recruiters.length > 0 && (
                    <button type="button" className="report-expand-all" onClick={toggleAll}>
                      {allExpanded ? 'Collapse all' : 'Expand all by customer'}
                    </button>
                  )}
                </div>
                <div className="table-wrapper mtn-scroll-x">
                  <table className="report-table report-table--recruiter">
                    <thead>
                      <tr className="report-group-row">
                        <th rowSpan="2">Recruiter</th>
                        <th colSpan="8" className="report-group">Calls &amp; texts</th>
                        <th colSpan="9" className="report-group report-sep">Pipeline</th>
                      </tr>
                      <tr>
                        <th title="Outbound calls">Calls out</th>
                        <th title="Outbound calls that lasted at least a minute">1 min+</th>
                        <th title="Total time on outbound calls">Talk time</th>
                        <th title="Texts the recruiter typed; CRM automated texts are not counted">Texts typed</th>
                        <th title="The same 40+ character message sent 5 or more times in this period">Copy-paste</th>
                        <th title="Different candidates texted">Cands texted</th>
                        <th title="Incoming calls answered">In answered</th>
                        <th title="Incoming calls missed or sent to voicemail">In missed</th>
                        <th className="report-sep">Screened</th>
                        <th className="pct-col">→ 5+</th>
                        <th>5+</th>
                        <th className="pct-col">→ FADV</th>
                        <th>FADV Sent</th>
                        <th className="pct-col">→ Sent</th>
                        <th>Sent to Client</th>
                        <th># FA Chats</th>
                        <th>Hired</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recruiters.length === 0 ? (
                        <tr><td colSpan="18" className="no-data">No data for selected date range</td></tr>
                      ) : (
                        <>
                          {fullView && (
                            <tr className="report-total-row">
                              <td className="recruiter-col">Total</td>
                              <MetricCells m={teamTotal} />
                            </tr>
                          )}
                          {recruiters.map(r => {
                            const open = expanded.has(r.id);
                            const you = !fullView && r.id === activity.viewerId;
                            const nameCell = (
                              <span className="report-name-cell">
                                <button type="button" className={`report-expand${open ? ' open' : ''}`} onClick={() => toggleRecruiter(r.id)}
                                  aria-expanded={open} aria-label={`${open ? 'Collapse' : 'Expand'} ${r.name} by customer`}>
                                  {open ? '−' : '+'}
                                </button>
                                <span title={r.name}>{firstName(r.name)}{you ? ' (you)' : ''}</span>
                              </span>
                            );
                            if (!open) {
                              return (
                                <tr key={r.id} className={you ? 'report-you-row' : ''}>
                                  <td className="recruiter-col">{nameCell}</td>
                                  <MetricCells m={r.total} />
                                </tr>
                              );
                            }
                            return (
                              <React.Fragment key={r.id}>
                                <tr className="report-recruiter-head">
                                  <td className="recruiter-col">{nameCell}</td>
                                  <td colSpan="17" className="report-station-count">
                                    {(activity.assignments?.[r.name] || []).length} assigned customer{(activity.assignments?.[r.name] || []).length === 1 ? '' : 's'}
                                  </td>
                                </tr>
                                {r.stations.map(st => (
                                  <tr key={st.name} className="report-station-row">
                                    <td className="client-col" title={st.title}>{st.name}</td>
                                    <MetricCells m={st.m} />
                                  </tr>
                                ))}
                                <tr className="report-station-row report-station-total">
                                  <td className="recruiter-col" title="Includes calls and texts to numbers that are not a candidate, so it can be more than the customer rows added up">
                                    {firstName(r.name)} total
                                  </td>
                                  <MetricCells m={r.total} />
                                </tr>
                              </React.Fragment>
                            );
                          })}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* By Company: admins and GV only */}
              {fullView && (
              <div className="table-section">
                <h3>Performance by Company</h3>
                <div className="table-wrapper mtn-scroll-x">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Company</th>
                        <th>Screened</th>
                        <th className="pct-col">→ 5+</th>
                        <th>5+</th>
                        <th className="pct-col">→ FADV</th>
                        <th>FADV Sent</th>
                        <th className="pct-col">→ Sent</th>
                        <th>Sent to Client</th>
                        <th># FA Chats</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(companyTotals).length === 0 ? (
                        <tr><td colSpan="9" className="no-data">No data for selected date range</td></tr>
                      ) : (
                        <>
                        <tr className="report-total-row">
                          <td className="client-col">Total</td>
                          <td className="metric-col">{companyGrandTotal.screened}</td>
                          <td className="pct-col">{pct(companyGrandTotal.rating_5_plus, companyGrandTotal.screened)}%</td>
                          <td className="metric-col">{companyGrandTotal.rating_5_plus}</td>
                          <td className="pct-col">{pct(companyGrandTotal.fadv_sent, companyGrandTotal.rating_5_plus)}%</td>
                          <td className="metric-col">{companyGrandTotal.fadv_sent}</td>
                          <td className="pct-col">{pct(companyGrandTotal.ready_for_interview, companyGrandTotal.fadv_sent)}%</td>
                          <td className="metric-col">{companyGrandTotal.ready_for_interview}</td>
                          <td className="metric-col">{faChatsCompanyTotal}</td>
                        </tr>
                        {Object.entries(companyTotals)
                          .sort((a, b) => b[1].screened - a[1].screened)
                          .map(([company, t]) => (
                            <tr key={company}>
                              <td className="client-col">{company}</td>
                              <td className="metric-col">{t.screened}</td>
                              <td className="pct-col">{pct(t.rating_5_plus, t.screened)}%</td>
                              <td className="metric-col">{t.rating_5_plus}</td>
                              <td className="pct-col">{pct(t.fadv_sent, t.rating_5_plus)}%</td>
                              <td className="metric-col">{t.fadv_sent}</td>
                              <td className="pct-col">{pct(t.ready_for_interview, t.fadv_sent)}%</td>
                              <td className="metric-col">{t.ready_for_interview}</td>
                              <td className="metric-col">{faChatsByCompany[company] || 0}</td>
                            </tr>
                          ))}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              )}

            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---- Performance by Recruiter (October 2026) ----
// Each recruiter row merges the RingCentral activity (GET /api/reports/recruiter-activity) with the
// pipeline funnel (recruiter-performance) and FA chats, overall and per customer. Locked users are
// left out. Calls/texts to numbers that aren't a candidate only count in the recruiter's total.
const METRIC_KEYS = ['calls_out', 'calls_1m', 'talk_sec', 'texts', 'copy_paste', 'cands_texted', 'in_answered', 'in_missed',
  'screened', 'rating_5_plus', 'fadv_sent', 'ready_for_interview', 'fa_chats', 'hired'];
const emptyMetrics = () => Object.fromEntries(METRIC_KEYS.map(k => [k, 0]));
const addMetrics = (acc, m) => { METRIC_KEYS.forEach(k => { acc[k] += Number(m[k]) || 0; }); return acc; };
const hasNumbers = m => METRIC_KEYS.some(k => m[k]);

function buildRecruiters(reportData, activity, faRows) {
  const byId = new Map();
  const rec = (id, name, locked) => {
    if (!byId.has(id)) byId.set(id, { id, name, locked: !!locked, total: emptyMetrics(), companies: new Map() });
    const r = byId.get(id);
    if (locked) r.locked = true;
    return r;
  };
  const add = (r, company, m) => {
    addMetrics(r.total, m);
    if (!company) return;
    if (!r.companies.has(company)) r.companies.set(company, emptyMetrics());
    addMetrics(r.companies.get(company), m);
  };
  for (const row of reportData) {
    if (row.recruiter_id == null) continue;
    const r = rec(Number(row.recruiter_id), row.recruiter_name, row.recruiter_locked);
    add(r, row.company_name, {
      screened: row.candidates_screened, rating_5_plus: row.rating_5_plus, fadv_sent: row.fadv_sent,
      ready_for_interview: row.ready_for_interview, hired: row.hired,
    });
  }
  for (const row of activity.rows || []) {
    add(rec(Number(row.recruiter_id), row.recruiter_name, row.locked), row.company_name, row);
  }
  for (const row of faRows) {
    const r = byId.get(Number(row.recruiter_id));
    if (r) add(r, row.company_name, { fa_chats: row.fa_chat_count });
  }
  const assignments = activity.assignments || {};
  return [...byId.values()]
    .filter(r => !r.locked && r.name)
    .map(r => {
      // Their assigned accounts, one row each; anything else they touched folds into one row
      const assigned = assignments[r.name] || [];
      const stations = assigned.map(name => ({ name, m: r.companies.get(name) || emptyMetrics() }));
      const other = emptyMetrics();
      const otherNames = [];
      for (const [name, m] of r.companies) {
        if (assigned.includes(name) || !hasNumbers(m)) continue;
        addMetrics(other, m);
        otherNames.push(name);
      }
      if (otherNames.length) stations.push({ name: 'Other customers', title: otherNames.sort().join(', '), m: other });
      return { ...r, stations };
    })
    .sort((a, b) => b.total.screened - a.total.screened || b.total.calls_out - a.total.calls_out);
}

const talkTime = sec => (sec < 3600 ? `${Math.round(sec / 60)} min` : `${(sec / 3600).toFixed(1)} hr`);
const pctOf = (num, den) => `${den > 0 ? Math.round((num / den) * 100) : 0}%`;

function MetricCells({ m }) {
  return (
    <>
      <td className="metric-col">{m.calls_out}</td>
      <td className="metric-col">{m.calls_1m}</td>
      <td className="metric-col">{talkTime(m.talk_sec)}</td>
      <td className="metric-col">{m.texts}</td>
      <td className="metric-col">{m.copy_paste}</td>
      <td className="metric-col">{m.cands_texted}</td>
      <td className="metric-col">{m.in_answered}</td>
      <td className="metric-col">{m.in_missed}</td>
      <td className="metric-col report-sep">{m.screened}</td>
      <td className="pct-col">{pctOf(m.rating_5_plus, m.screened)}</td>
      <td className="metric-col">{m.rating_5_plus}</td>
      <td className="pct-col">{pctOf(m.fadv_sent, m.rating_5_plus)}</td>
      <td className="metric-col">{m.fadv_sent}</td>
      <td className="pct-col">{pctOf(m.ready_for_interview, m.fadv_sent)}</td>
      <td className="metric-col">{m.ready_for_interview}</td>
      <td className="metric-col">{m.fa_chats}</td>
      <td className="metric-col">{m.hired}</td>
    </>
  );
}

export default Reports;
